/**
 * 教材领取 · **待领取列表**（服务台入口 `/logistics/track`）
 * -----------------------------------------------------------------------------
 * 2026-10-22 改版（用户拍板）：教材是**按报名单**领的，不是按账号领的。
 * 原来这一页直接落到「最近一张报名单」的收件信息表单，等于整个 App 只有一份领取信息，
 * 有三张报名单就有两张领不了。现在：
 *
 *   本页（列表，一单一行）→ 点进去才是该单的领取页 `/logistics/:registrationId`
 *
 * 只列**报名成功**（`ENROLLED`）的单子：缴费没确认之前不安排发放，列出来也点不动，
 * 徒增「为什么领不了」的困惑。草稿 / 待缴费请走「报名记录」。
 *
 * 排序：按「该不该我动手」排 —— 待申请领取（该你点）→ 待发货（等仓库）→ 已发货（等签收）
 * → 异常（要联系客服）→ 已领取（已完成沉底）。
 *
 * ⚠️ 2026-10-22 教材状态改为**独立列**四态（待申请领取 / 待发货 / 已发货 / 已领取），
 * 不再是「未发放 / 已发货 / 已签收 / 已领取」那套派生值。
 */
import { MaterialStatus } from '@enroll/shared';
import { useNavigate } from 'react-router-dom';
import { useMyRegistrations } from '../api/hooks';
import { BoxIcon, ChevronRightIcon } from '../components/icons';
import { EmptyHint, LoadingBlock, PageShell } from '../components/layout';
import { materialStepText } from '../lib/flow';

/** 教材状态 → 胶囊配色（`.pill--*` 与报名单状态共用一套色阶） */
function materialTone(status: string): string {
  switch (status) {
    case MaterialStatus.RECEIVED:
      return 'success';
    case MaterialStatus.ABNORMAL:
      return 'danger';
    case MaterialStatus.PENDING_APPLY:
      // 「该你动手」用 warning 而不是 info，和「等别人」区分开
      return 'warning';
    default:
      return 'info';
  }
}

/** 已到手 = 已领取（签收与现场领取在学员视角是同一件事） */
function isReceived(status: string | undefined): boolean {
  return status === MaterialStatus.RECEIVED;
}

/** 排序权重：数字小的在前 */
function materialRank(status: string | undefined): number {
  switch (status) {
    case MaterialStatus.PENDING_APPLY:
      return 0;
    case MaterialStatus.PENDING_SHIP:
      return 1;
    case MaterialStatus.SHIPPED:
      return 2;
    case MaterialStatus.ABNORMAL:
      return 3;
    case MaterialStatus.RECEIVED:
      return 4;
    default:
      return 0;
  }
}

export default function LogisticsList() {
  const navigate = useNavigate();
  const mine = useMyRegistrations();

  if (mine.isPending) {
    return (
      <PageShell title="教材领取" back solidNav>
        <LoadingBlock text="正在加载可领取的报名单" />
      </PageShell>
    );
  }

  const items = (mine.data?.items ?? []).filter((item) => item.status === 'ENROLLED');
  const pending = items.filter((item) => !isReceived(item.materialStatus));
  const received = items.filter((item) => isReceived(item.materialStatus));
  const ordered = [...items].sort(
    (a, b) => materialRank(a.materialStatus) - materialRank(b.materialStatus),
  );

  return (
    <PageShell title="教材领取" back solidNav>
      {ordered.length === 0 ? (
        <EmptyHint
          icon={<BoxIcon size={34} strokeWidth={1.5} />}
          title="暂无可领取的教材"
          description="报名成功（缴费确认）后，教材会出现在这里，按报名单逐单领取"
          actionText="查看我的报名"
          onAction={() => navigate('/registration/list')}
          fill
        />
      ) : (
        <>
          <div className="section-label">
            待办 {pending.length} 单 · 已完成 {received.length} 单
          </div>
          {ordered.map((item) => {
            const status = item.materialStatus ?? MaterialStatus.PENDING_APPLY;
            const done = isReceived(status);
            return (
              <button
                key={item.id}
                type="button"
                className="record-card"
                onClick={() => navigate(`/logistics/${item.id}`)}
              >
                <span className="record-card__head">
                  <span className="record-card__no">{item.registrationNo}</span>
                  <span className={`pill pill--${materialTone(status)}`}>
                    {materialStepText(status)}
                  </span>
                </span>
                <span className="record-card__meta">
                  {item.enrollTypeName} · {item.batchName}
                </span>
                <span className="record-card__foot">
                  <span className="record-card__amount-label">邮寄 · 运费到付</span>
                  <span className="record-card__more">
                    {done
                      ? '查看'
                      : status === MaterialStatus.PENDING_APPLY
                        ? '去领取'
                        : '查看进度'}
                    <ChevronRightIcon size={12} />
                  </span>
                </span>
              </button>
            );
          })}
          {/* 一期不接快递商系统 —— 这句是给学员的预期管理，别删 */}
          <p className="card__desc">
            提交领取后由仓库安排发货，寄出会通过公众号通知；学员端不显示运单号，运费签收时到付。
            发货满 5 天自动确认已领取。
          </p>
        </>
      )}
    </PageShell>
  );
}
